// Model-aware helpers shared by the holdings views. Pure: the same tree the adapter
// builds (`buildHoldingsTree`) is what every tab groups by, so a model can never be
// one thing in By Fund and another in By Source.
import { investableFunds } from './portfolio.adapters';

/**
 * The tree when there is one; otherwise every investable fund as its own top-level
 * item, so a caller with only a flat fund list renders exactly as before.
 */
export const treeOrFlat = (tree, funds = []) => {
  if (tree && tree.length) return tree;
  return investableFunds(funds).map((f) => ({
    ...f,
    type: 'fund',
    key: f.ticker,
    balancePct: f.pct,
    electionPct: f.election,
    children: [],
  }));
};

/**
 * One money source's own holdings (FM-3085): the tree cut down to the funds that source
 * holds, each at its real balance and share of the source. A model keeps only its members
 * held here, and its share is its balance over the source's total — summing the rounded
 * member shares read 100.01%. A fund the tree doesn't list becomes its own line.
 *
 * @param {Array} items top-level tree items (`treeOrFlat`)
 * @param {Array} sourceFunds `[{ ticker, name, balance, pct }]` for one source
 * @param {number} total the source's own balance
 */
export const sourceHoldings = (items = [], sourceFunds = [], total = 0) => {
  const held = new Map(sourceFunds.filter((f) => f.balance).map((f) => [f.ticker, f]));
  const placed = new Set();
  const round2 = (v) => Math.round(v * 100) / 100;
  // The view's share of the source when it sends one; else from the balances, never 0.
  const shareOf = (f) => (f.pct != null ? f.pct : total ? round2((f.balance / total) * 100) : null);
  const inSource = (it) => {
    const f = held.get(it.ticker);
    placed.add(it.ticker);
    return { ...it, balance: f.balance, pct: shareOf(f), balancePct: shareOf(f) };
  };

  const out = items.flatMap((it) => {
    if (it.type !== 'model') return held.has(it.ticker) ? [inSource(it)] : [];
    const children = (it.children || []).filter((c) => held.has(c.ticker)).map(inSource);
    if (!children.length) return [];
    const balance = round2(children.reduce((s, c) => s + (c.balance || 0), 0));
    return [{ ...it, children, balance, balancePct: total ? round2(Math.min(balance / total, 1) * 100) : null }];
  });
  held.forEach((f, ticker) => {
    if (!placed.has(ticker)) out.push({ ...f, type: 'fund', key: ticker, pct: shareOf(f), balancePct: shareOf(f), children: [] });
  });
  return out.sort((a, b) => (b.balance || 0) - (a.balance || 0));
};

/**
 * Flatten tree items to table rows: each top-level item, then — when a model is
 * open — its underlying funds (`_child`, `_modelKey`) and an optional note row.
 *
 * @param {Array} items top-level tree items
 * @param {(key: string) => boolean} isOpen whether a model is expanded
 * @param {object} [opts]
 * @param {(child, model) => object} [opts.child] extra fields for a child row
 * @param {(model) => object|null} [opts.note] a closing note row for an open model
 */
export const flattenRows = (items = [], isOpen, { child, note } = {}) => {
  const rows = [];
  items.forEach((it) => {
    if (it.type !== 'model') {
      rows.push(it);
      return;
    }
    rows.push({ ...it, _model: true });
    if (!isOpen(it.key)) return;
    (it.children || []).forEach((c) =>
      rows.push({ ...c, _child: true, _modelKey: it.key, ...(child ? child(c, it) : null) }),
    );
    const n = note ? note(it) : null;
    if (n) rows.push({ ...n, _note: true, _modelKey: it.key });
  });
  return rows;
};

/**
 * Split a set of funds (e.g. one asset class's members) by the model they are held
 * inside: `groups` in tree order, `standalone` for funds with no model in the tree —
 * the same rule `buildHoldingsTree` applies to an undeclared model.
 */
export const partitionByModel = (funds = [], items = []) => {
  const models = items.filter((it) => it.type === 'model');
  const groups = models
    .map((model) => ({ model, funds: funds.filter((f) => f.modelId === model.key) }))
    .filter((g) => g.funds.length);
  const grouped = new Set(groups.flatMap((g) => g.funds));
  return { groups, standalone: funds.filter((f) => !grouped.has(f)) };
};
